// Lightweight local cache layer for API responses.
//
// Goals:
//  - Avoid re-hitting the server for data that rarely changes within a short
//    window (salons list, categories, salon details).
//  - Cache the user's reservations and invalidate them the moment a booking is
//    created / cancelled / rescheduled.
//
// Entries live both in-memory (fast, survives SPA navigation) and in
// localStorage (survives full page reloads / PWA relaunch). Each entry carries
// its own expiry so different data types can use different freshness windows.

const STORAGE_PREFIX = 'adwary_cache_';
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const memoryStore = new Map();

function isStale(entry) {
  return !entry || typeof entry.expiresAt !== 'number' || Date.now() > entry.expiresAt;
}

function readFromStorage(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeToStorage(key, entry) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exceeded or private-mode: in-memory cache still works.
  }
}

function removeFromStorage(key) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    // ignore
  }
}

export const cache = {
  get(key) {
    let entry = memoryStore.get(key);
    if (!entry) {
      entry = readFromStorage(key);
      if (entry) memoryStore.set(key, entry);
    }
    if (isStale(entry)) {
      this.invalidate(key);
      return null;
    }
    return entry.data;
  },

  set(key, data, ttlMs = DEFAULT_TTL_MS) {
    const entry = { data, expiresAt: Date.now() + ttlMs };
    memoryStore.set(key, entry);
    writeToStorage(key, entry);
  },

  invalidate(key) {
    memoryStore.delete(key);
    removeFromStorage(key);
  },

  invalidatePrefix(prefix) {
    for (const key of Array.from(memoryStore.keys())) {
      if (key.startsWith(prefix)) memoryStore.delete(key);
    }
    try {
      const fullPrefix = STORAGE_PREFIX + prefix;
      Object.keys(localStorage)
        .filter((k) => k.startsWith(fullPrefix))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  },

  invalidateAll() {
    memoryStore.clear();
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(STORAGE_PREFIX))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  },
};

// Freshness windows per data type. Kept uniform at 30s so cached data is never
// more than half a minute stale, while still avoiding repeated server hits
// during quick navigation.
export const CacheTTL = {
  salons: 30 * 1000, // salon list / details
  categories: 30 * 1000,
  bookings: 30 * 1000, // reservations are user-specific and time-sensitive
};

const BOOKINGS_PREFIX = 'bookings:';

export const CacheKeys = {
  businesses: 'accounts:list',
  categories: 'categories:list',
  business: (id) => `account:${id}`,
  bookingsAll: `${BOOKINGS_PREFIX}all`,
  myBookings: (businessId) => `${BOOKINGS_PREFIX}my:${businessId || 'default'}`,
};

// Call after ANY reservation mutation (create / cancel / reschedule) so the
// next read fetches fresh data from the server.
export function invalidateBookingsCache() {
  cache.invalidatePrefix(BOOKINGS_PREFIX);
}
